import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useTicketingIntegrations from "../../../../api/hooks/useTicketingIntegrations";
import {
  asanaTicketKeys,
  jiraIssuesKeys,
  serviceNowTicketKeys,
  ticketKeys,
} from "../../../../api/queryKeys";
import { Toast } from "../../../../components/Toast";
import { buildLinkToGap } from "../../../../utils/utils";
import { getIncludedEntityIds } from "../../../posture/controls/components/EntitiesSection/utils";
import IssueLinkingModal from "../Jira/IssueLinkingModal";
import AsanaButton from "./AsanaButton";
import AsanaTaskCreationModal from "./AsanaTaskCreationModal";

export default function AsanaInteractionManager({
  fullControl,
  sx,
  hideTitle = false,
}) {
  // Use variables
  const queryClient = useQueryClient();
  const {
    createAsanaTask,
    trackEvent,
    getTicketByControlID,
    getJiraIssueByControlID,
    getServiceNowIncidentByControlID,
  } = useAPI();

  // States
  const [isAsanaTaskCreationModalOpen, setIsAsanaTaskCreationModalOpen] =
    useState(false);
  const [isAsanaTaskLinkingModalOpen, setIsAsanaTaskLinkingModalOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    isJiraIntegrated,
    isAsanaIntegrated,
    isServiceNowIntegrated,
    isLoading: isLoadingIntegrations,
  } = useTicketingIntegrations();

  const { data: ticket, isLoading: isLoadingTicket } = useQuery({
    queryKey: asanaTicketKeys.asanaTicketControl(fullControl?.control_id),
    queryFn: async () => {
      const issue = (await getTicketByControlID(fullControl?.control_id)).data;
      return issue;
    },
    enabled:
      fullControl !== null && isAsanaIntegrated && !isLoadingIntegrations,
  });

  const { data: jiraIssue, isLoading: isLoadingJiraIssue } = useQuery({
    queryKey: jiraIssuesKeys.jiraIssueControl(fullControl?.control_id),
    queryFn: async () => {
      const issue = (await getJiraIssueByControlID(fullControl?.control_id))
        .data;
      return issue;
    },
    enabled: fullControl !== null && isJiraIntegrated && !isLoadingIntegrations,
  });

  const { data: serviceNowIncident, isLoading: isLoadingServiceNowIncident } =
    useQuery({
      queryKey: serviceNowTicketKeys.serviceNowTicketControl(
        fullControl?.control_id
      ),
      queryFn: async () => {
        const incident = (
          await getServiceNowIncidentByControlID(fullControl?.control_id)
        ).data;
        return incident;
      },
      enabled:
        fullControl !== null &&
        isServiceNowIntegrated &&
        !isLoadingIntegrations,
    });

  const createAsanaTaskFn = () => {
    setIsAsanaTaskCreationModalOpen(true);
    trackEvent("User opened Asana task creation modal").then();
  };

  const handleCloseAsanaTaskCreationModal = () => {
    setIsAsanaTaskCreationModalOpen(false);
    trackEvent("User closed Asana task creation modal").then();
  };

  const handleCreateTask = async (taskData) => {
    setIsLoading(true);

    if (taskData.includeEntities) {
      taskData.notes =
        taskData.notes +
        "\n\n_Note: The misconfigured entities can be found in the attached CSV file_";
    }

    const taskContent = {
      name: taskData.taskName,
      projects: taskData.projects.map((project) => project.gid),
      assignee: taskData.assignee?.gid,
      notes: taskData.notes,
      entity_ids: taskData.includeEntities
        ? getIncludedEntityIds(fullControl)
        : [],
      link_to_gap: fullControl?.control_id
        ? buildLinkToGap(fullControl?.control_id)
        : "",
    };

    try {
      const res = await createAsanaTask(taskContent);
      await trackEvent("User created Asana task");
      Toast.success({
        text: "Asana task created successfully!",
        recommended_action: "View",
        recommended_action_callback: () => {
          const taskUrl = res?.data?.url;
          if (taskUrl) {
            window.open(taskUrl, "_blank");
          }
        },
      });
      queryClient.invalidateQueries({
        queryKey: asanaTicketKeys.asanaTicketControl(fullControl?.control_id),
      });
      queryClient.invalidateQueries({
        queryKey: ticketKeys.all,
      });
      handleCloseAsanaTaskCreationModal();
    } catch (err) {
      Toast.error(
        "Failed to create Asana task. Please try again or contact support."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const linkAsanaTask = () => {
    setIsAsanaTaskLinkingModalOpen(true);
    trackEvent("User opened Asana task linking modal").then();
  };

  const handleCloseAsanaTaskLinkingModal = () => {
    setIsAsanaTaskLinkingModalOpen(false);
    trackEvent("User closed Asana task linking modal").then();
  };

  return (
    <>
      {/* ASANA BUTTON */}
      {isAsanaIntegrated && (
        <AsanaButton
          fullControl={fullControl}
          createAsanaTask={createAsanaTaskFn}
          linkAsanaTask={linkAsanaTask}
          asanaTaskLink={ticket?.asana_details?.task_link || null}
          loading={
            isLoadingIntegrations ||
            isLoadingTicket ||
            isLoadingJiraIssue ||
            isLoadingServiceNowIncident
          }
          sx={sx}
          hideTitle={hideTitle}
          disabled={!!jiraIssue?.source || !!serviceNowIncident?.source}
        />
      )}

      {/* ASANA CREATION MODAL */}
      {isAsanaTaskCreationModalOpen && (
        <AsanaTaskCreationModal
          open={isAsanaTaskCreationModalOpen}
          onClose={handleCloseAsanaTaskCreationModal}
          onSubmit={handleCreateTask}
          isLoading={isLoading}
          fullControl={fullControl}
        />
      )}

      {/* ASANA LINK EXISTING TASK MODAL */}
      {isAsanaTaskLinkingModalOpen && (
        <IssueLinkingModal
          open={isAsanaTaskLinkingModalOpen}
          onClose={handleCloseAsanaTaskLinkingModal}
          gapId={fullControl?.control_id}
          tool="Asana"
          ticketType="task"
        />
      )}
    </>
  );
}
